import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils.js';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium',
    'transition-all duration-200 ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-background)]',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default: [
          'bg-[var(--color-primary)] text-[var(--color-primary-foreground)]',
          'shadow-[0_1px_2px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.1)]',
          'hover:bg-[var(--color-primary)]/90 hover:shadow-[0_4px_12px_rgba(59,130,246,0.25)]',
          'active:scale-[0.98] active:shadow-[0_1px_2px_rgba(0,0,0,0.3)]',
        ].join(' '),
        destructive: [
          'bg-[var(--color-destructive)] text-[var(--color-destructive-foreground)]',
          'shadow-[0_1px_2px_rgba(0,0,0,0.3)]',
          'hover:bg-[var(--color-destructive)]/90 hover:shadow-[0_4px_12px_rgba(220,38,38,0.25)]',
          'active:scale-[0.98]',
        ].join(' '),
        outline: [
          'border border-[var(--color-border)] bg-transparent text-[var(--color-foreground)]',
          'hover:bg-[var(--color-secondary)] hover:border-[var(--color-muted-foreground)]/30',
          'active:scale-[0.98]',
        ].join(' '),
        secondary: [
          'bg-[var(--color-secondary)] text-[var(--color-secondary-foreground)]',
          'shadow-[0_1px_2px_rgba(0,0,0,0.2)]',
          'hover:bg-[var(--color-muted)]',
          'active:scale-[0.98]',
        ].join(' '),
        ghost: [
          'text-[var(--color-muted-foreground)]',
          'hover:bg-[var(--color-secondary)] hover:text-[var(--color-foreground)]',
          'active:scale-[0.98]',
        ].join(' '),
        link: [
          'text-[var(--color-primary)] underline-offset-4',
          'hover:underline',
        ].join(' '),
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-11 rounded-lg px-8 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
